import React, { useEffect, useState } from "react";

import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";

import { getInitials } from "@/src/components/common/AvatarStack";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/src/components/ui/command";
import { FieldError } from "@/src/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/lib/utils";

import type { JSX } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

import type { ComboboxOption } from "./MultiCombobox";

export interface AvatarComboboxOption extends ComboboxOption {
  avatarUrl?: string | null;
}

function CenteredSpinner({ className }: { className?: string }): JSX.Element {
  return (
    <div className={cn("flex justify-center", className)}>
      <Loader2 className="text-muted-foreground size-4 animate-spin" />
    </div>
  );
}

function OptionAvatar({
  option,
  className,
}: {
  option: AvatarComboboxOption;
  className?: string;
}): JSX.Element {
  return (
    <Avatar className={cn("size-6", className)}>
      <AvatarImage alt="" src={option.avatarUrl ?? undefined} />
      <AvatarFallback>{getInitials(option.label)}</AvatarFallback>
    </Avatar>
  );
}

const LOAD_MORE_SCROLL_THRESHOLD = 48;

interface MultiComboboxWithAvatarBaseProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: AvatarComboboxOption[];
  onSearchChange: (search: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  popoverContainer?: HTMLElement | null;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export function MultiComboboxWithAvatarBase({
  value,
  onChange,
  options,
  onSearchChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  isLoading,
  disabled,
  error,
  popoverContainer,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
}: MultiComboboxWithAvatarBaseProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Accumulate every option we have ever seen so a selected chip keeps its
  // display data (label/avatar) even once the current results no longer include
  // it.
  const [seenOptions, setSeenOptions] = useState<
    Record<string, AvatarComboboxOption>
  >({});

  useEffect(() => {
    if (options.length === 0) return;
    setSeenOptions((current) => {
      const next = { ...current };
      options.forEach((option) => {
        next[option.value] = option;
      });
      return next;
    });
  }, [options]);

  const resolveOption = (
    optionValue: string,
  ): AvatarComboboxOption | undefined =>
    options.find((option) => option.value === optionValue) ??
    seenOptions[optionValue];

  const toggleValue = (optionValue: string): void => {
    if (value.includes(optionValue)) {
      onChange(value.filter((selectedValue) => selectedValue !== optionValue));
      return;
    }
    onChange([...value, optionValue]);
  };

  const handleSearchChange = (nextSearch: string): void => {
    setSearch(nextSearch);
    onSearchChange(nextSearch);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_SCROLL_THRESHOLD) {
      onLoadMore();
    }
  };

  const renderEmptyState = (): JSX.Element => {
    if (isLoading) {
      return <CenteredSpinner className="py-6" />;
    }
    return <CommandEmpty>{emptyLabel}</CommandEmpty>;
  };

  return (
    <React.Fragment>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            aria-invalid={error ? true : undefined}
            className="bg-card h-auto min-h-10 w-full items-center justify-between gap-2 py-1 text-left whitespace-normal"
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            {value.length > 0 ? (
              <div className="flex flex-1 flex-wrap gap-1">
                {value.map((optionValue) => {
                  const option = resolveOption(optionValue);
                  const label = option ? option.label : optionValue;
                  return (
                    <Badge
                      key={optionValue}
                      className="gap-1.5 py-0.5 pr-1 pl-1"
                      variant="secondary"
                    >
                      {option ? <OptionAvatar option={option} /> : null}
                      {label}
                      <span
                        aria-label={label}
                        className="hover:bg-foreground/10 rounded-full p-0.5"
                        onPointerDown={(event) => event.stopPropagation()}
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleValue(optionValue);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleValue(optionValue);
                          }
                        }}
                      >
                        <X className="size-3" />
                      </span>
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted-foreground flex-1 text-left">
                {placeholder}
              </span>
            )}
            <ChevronsUpDown className="mt-0.5 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-(--radix-popover-trigger-width) p-0"
          container={popoverContainer}
        >
          {/* No client-side filtering: the parent owns the search (server-side
              via `onSearchChange`) and supplies the already-filtered `options`. */}
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={handleSearchChange}
              placeholder={searchPlaceholder}
              value={search}
            />
            <CommandList onScroll={handleScroll}>
              {renderEmptyState()}
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={(): void => toggleValue(option.value)}
                    value={option.value}
                  >
                    <Check
                      className={cn(
                        "mr-1 size-4",
                        value.includes(option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <OptionAvatar option={option} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
              {isFetchingNextPage ? <CenteredSpinner className="py-2" /> : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError>{error}</FieldError>
    </React.Fragment>
  );
}

interface MultiComboboxWithAvatarProps<T extends FieldValues> {
  name: FieldPathByValue<T, string[] | undefined>;
  options: AvatarComboboxOption[];
  onSearchChange: (search: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  isLoading?: boolean;
  disabled?: boolean;
  popoverContainer?: HTMLElement | null;
}

/**
 * Searchable multi-select with removable avatar chips, bound to a
 * react-hook-form `string[]` field via `useController`. The validation error is
 * read from the field state. Mirrors `MultiCombobox`, but renders avatars and
 * expects its `options` to be supplied/refreshed by the parent (server search).
 */
export function MultiComboboxWithAvatar<T extends FieldValues>({
  name,
  options,
  onSearchChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  isLoading,
  disabled,
  popoverContainer,
}: MultiComboboxWithAvatarProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <MultiComboboxWithAvatarBase
      disabled={disabled}
      emptyLabel={emptyLabel}
      error={fieldState.error?.message}
      isLoading={isLoading}
      onChange={field.onChange}
      onSearchChange={onSearchChange}
      options={options}
      placeholder={placeholder}
      popoverContainer={popoverContainer}
      searchPlaceholder={searchPlaceholder}
      value={field.value ?? []}
    />
  );
}
